/* =========================================================
   CHAGUAL — Teclado y anuncio del buscador predictivo

   Qué resuelve (B6 de la auditoría 22, medido en la vista previa antes de tocar):
     · Las sugerencias no se recorrían con las flechas: había que tabular una por
       una. Medido: `ArrowDown` sobre el campo no movía el foco.
     · `Escape` BORRABA lo escrito. Medido: con «macetero» en el campo, un
       `Escape` lo dejaba vacío.
     · Nada anunciaba que aparecieron sugerencias ni cuántas.
     · El formulario no declaraba `role="search"` (se arregla en el snippet).

   Por qué vive aquí y no en `assets/utilities.js`:
   `utilities.js` (y su gemelo `.js.liquid`) es el owner del buscador predictivo,
   pero está fuera de alcance para este lote por trabajo en curso. Esta capa es
   ADITIVA: no reemplaza nada de `utilities.js`, se apoya en su máquina de estados
   y si este archivo no carga, el buscador sigue funcionando exactamente como hoy.

   El detalle que obliga a ser cuidadoso: el panel se muestra u oculta con el
   atributo `data-animation-state` que escribe la instancia de `Animation` que
   `utilities.js` guarda en una variable local. Esa instancia lleva su propio
   estado EN MEMORIA y `animateTo` compara contra él. Si desde fuera se escribiera
   el atributo a mano, su estado quedaría desincronizado y el panel ya no volvería
   a abrirse hasta recargar. Por eso, para cerrar, NO se toca el atributo: se
   vuelve a lanzar el mismo `keyup` de Escape sobre `window` —que es donde
   `utilities.js` escucha— y se deja que cierre con SU propia instancia. Lo único
   que hace este archivo es devolver el texto que ese cierre borra.
   ========================================================= */
(function () {
  'use strict';

  // Guarda de carga única: el snippet del buscador se renderiza en varias
  // superficies (cabecera, móvil, blog, 404, página de búsqueda) y cada una emite
  // su etiqueta de script.
  if (window.ChagualPredictiveSearchA11y) return;
  window.ChagualPredictiveSearchA11y = true;

  var SELECTOR_FORM = '[data-autocomplete-true]';
  var SELECTOR_INPUT = '[data-search-input]';
  var SELECTOR_PANEL = '[data-predictive-search]';
  var SELECTOR_RESULTADOS = '[data-predictive-search-results]';
  var SELECTOR_ESTADO = '[data-ch-search-status]';
  // Las sugerencias son enlaces normales servidos por `sections/predictive-search.liquid`.
  // El botón «Buscar "…"» del final también es una parada legítima del recorrido.
  var SELECTOR_OPCIONES = 'a.predictive-search__list-item-anchor, [data-search-button]';

  // No se filtra por «está pintado»: el panel tarda en aparecer (transición de
  // 0,5 s) y `utilities.js` marca el formulario como activo desde el primer
  // instante, así que una comprobación de geometría dejaría la lista vacía justo
  // cuando la persona pulsa la flecha. Quien decide si algo puede recibir el foco
  // es el navegador: `mover` comprueba si el foco llegó de verdad y, si no llegó,
  // deja pasar la tecla en vez de tragársela.
  function opciones(form) {
    var panel = form.querySelector(SELECTOR_PANEL);
    if (!panel) return [];
    return Array.prototype.slice.call(panel.querySelectorAll(SELECTOR_OPCIONES))
      .filter(function (el) { return !el.hasAttribute('hidden'); });
  }

  function estaAbierto(form) {
    return form.classList.contains('search-form--active');
  }

  function enfocar(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (error) { el.focus(); }
  }

  // ---------------------------------------------------------------
  // Anuncio del resultado. La región viva vive FUERA de
  // `[data-predictive-search-results]` a propósito: ese contenedor lo reemplaza
  // entero `utilities.js`, y una región viva que nace junto a su contenido no se
  // anuncia (es el mismo error que la auditoría 22 registró en la vitrina, B16).
  // ---------------------------------------------------------------
  function anunciar(form) {
    var estado = form.querySelector(SELECTOR_ESTADO);
    if (!estado) return;
    if (!estaAbierto(form)) { estado.textContent = ''; return; }
    var total = opciones(form).filter(function (el) {
      return !el.hasAttribute('data-search-button');
    }).length;
    var texto;
    if (total === 0) texto = 'Sin sugerencias.';
    else if (total === 1) texto = '1 sugerencia. Usa las flechas para recorrerlas.';
    else texto = total + ' sugerencias. Usa las flechas para recorrerlas.';
    if (estado.textContent !== texto) estado.textContent = texto;
  }

  // ---------------------------------------------------------------
  // Cerrar sin borrar. Ver la nota de cabecera: el cierre lo hace `utilities.js`
  // con su propia instancia de animación; aquí sólo se conserva el texto.
  // ---------------------------------------------------------------
  function cerrarConservandoTexto(form) {
    var campos = Array.prototype.slice.call(document.querySelectorAll(SELECTOR_INPUT));
    var valores = campos.map(function (campo) { return campo.value; });
    var input = form.querySelector(SELECTOR_INPUT);

    try {
      // `utilities.js` escucha `keyup` en `window`; al lanzarlo sobre `window` el
      // evento no pasa por `document`, así que este mismo archivo no lo vuelve a
      // interceptar y no hay bucle.
      window.dispatchEvent(new KeyboardEvent('keyup', {
        key: 'Escape', code: 'Escape', bubbles: false, cancelable: true
      }));
    } catch (error) {
      // Navegador sin constructor de KeyboardEvent: se cierra lo que se pueda.
      form.classList.remove('search-form--active');
    }

    // Devolver el texto que el cierre de `utilities.js` borra. Se asigna el valor
    // sin lanzar `input`, para no disparar otra búsqueda ni reabrir el panel.
    campos.forEach(function (campo, i) {
      if (campo.value !== valores[i]) campo.value = valores[i];
    });

    if (input) enfocar(input);
    anunciar(form);
  }

  // Devuelve true sólo si el foco llegó realmente a la sugerencia. El navegador se
  // niega a enfocar lo que no está visible, así que esto también cubre el instante
  // en que el panel todavía se está abriendo.
  function mover(form, desde, paso) {
    var lista = opciones(form);
    if (!lista.length) return false;
    var i = lista.indexOf(desde);
    var siguiente;
    if (i === -1) siguiente = paso > 0 ? lista[0] : lista[lista.length - 1];
    else siguiente = lista[(i + paso + lista.length) % lista.length];
    if (!siguiente) return false;
    enfocar(siguiente);
    return document.activeElement === siguiente;
  }

  function alTeclear(event, form) {
    var key = event.key;
    var input = form.querySelector(SELECTOR_INPUT);
    var enElCampo = event.target === input;

    if (!estaAbierto(form)) return;

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      var paso = key === 'ArrowDown' ? 1 : -1;
      // Desde el campo, la flecha entra a la lista por el extremo que corresponde.
      if (mover(form, enElCampo ? null : event.target, paso)) event.preventDefault();
      return;
    }

    if (enElCampo) return;

    if (key === 'Home' || key === 'End') {
      var lista = opciones(form);
      if (!lista.length) return;
      var extremo = key === 'Home' ? lista[0] : lista[lista.length - 1];
      enfocar(extremo);
      if (document.activeElement === extremo) event.preventDefault();
      return;
    }

    // Desde una sugerencia, cualquier tecla de escritura devuelve el foco al campo
    // para seguir afinando la consulta sin usar el ratón.
    if (key === 'Backspace' || (key && key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey)) {
      enfocar(input);
    }
  }

  function conectar(form) {
    if (form.hasAttribute('data-ch-search-a11y')) return;
    form.setAttribute('data-ch-search-a11y', 'true');

    // Las flechas van en `keydown` (es donde se puede frenar el desplazamiento de
    // la página y donde la repetición de tecla se comporta bien).
    form.addEventListener('keydown', function (event) { alTeclear(event, form); }, true);

    // Escape va en `keyup`, el MISMO evento que escucha `utilities.js`, y en fase
    // de captura sobre el formulario: así se detiene antes de llegar al manejador
    // de `window` que borra el texto. Manejarlo en `keydown` provocaría que el
    // `keyup` real llegara después y volviera a borrar. Sólo se intercepta con el
    // panel abierto en ESTE formulario: con el panel cerrado el Escape sigue su
    // camino normal y el menú móvil, los cajones de filtro y el mini-carrito lo
    // reciben como siempre.
    form.addEventListener('keyup', function (event) {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      if (!estaAbierto(form)) return;
      event.stopPropagation();
      cerrarConservandoTexto(form);
    }, true);

    var resultados = form.querySelector(SELECTOR_RESULTADOS);
    if (resultados && window.MutationObserver) {
      new MutationObserver(function () { anunciar(form); })
        .observe(resultados, { childList: true, subtree: true });
    }
    // `utilities.js` abre y cierra el panel con una clase en el formulario.
    if (window.MutationObserver) {
      new MutationObserver(function () { anunciar(form); })
        .observe(form, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR_FORM), conectar);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
